<template>
  <b-modal
    v-model="show"
    @shown="onShown"
    title="Share Project"
    centered
    size="lg"
    :no-close-on-backdrop="submitting"
    :no-close-on-esc="submitting"
  >
    <b-alert
      v-if="shareAlertMessage"
      :variant="shareAlertVariant"
      dismissible
      class="share-project-modal-alert mb-3 text-left"
      @dismissed="onDismissShareAlert"
    >
      {{ shareAlertMessage }}
    </b-alert>

    <b-form-group
      label="Recipient email"
      :invalid-feedback="emailInvalidFeedback"
      :state="emailFieldState"
    >
      <b-form-input
        v-model.trim="sharedToEmail"
        type="email"
        autocomplete="email"
        placeholder="Enter the recipient's email"
        debounce="400"
        :state="emailFieldState"
        :disabled="submitting"
      />
      <small v-if="isCheckingRecipientEmail" class="text-muted d-block mt-1">
        Checking email...
      </small>
      <small
        v-else-if="recipientHint"
        :class="['d-block', 'mt-1', recipientHintClass]"
      >
        {{ recipientHint }}
      </small>
    </b-form-group>

    <b-form-group label="Search project">
      <b-form-input
        v-model.trim="projectSearch"
        debounce="400"
        placeholder="Search project by name"
        class="mb-2 mt-2"
        :disabled="submitting"
      />
    </b-form-group>

    <b-alert
      v-if="noTradiesInProjects"
      show
      variant="warning"
      class="mb-3 text-left"
    >
      {{ noTradiesInProjectsMessage }}
    </b-alert>

    <div
      class="mass-notif-select-all d-flex align-items-center w-100 mass-notif-row mass-notif-row--user"
    >
      <b-form-checkbox
        :indeterminate="someSelectedOnPage && !allSelectedOnPage"
        :checked="allSelectedOnPage"
        :disabled="isLoading || submitting || projects.length === 0"
        @change="toggleSelectAll"
        class="mass-notif-checkbox"
      >
        &nbsp;
      </b-form-checkbox>
      <span class="mass-notif-label">Select all on this page</span>
    </div>

    <hr class="mass-notif-divider" />

    <b-overlay :show="isLoading || submitting" rounded="sm" spinner-small>
      <template #overlay>
        <div class="text-center text-primary my-2">
          <digger-loader />
          <p class="mt-2 text-muted mb-0">
            {{ submitting ? 'Sharing projects...' : 'Loading projects...' }}
          </p>
        </div>
      </template>

      <div
        class="mass-notif-list"
        style="min-height: 220px; max-height: 50vh; overflow-y: auto"
      >
        <div
          v-for="project in projects"
          :key="'proj-' + project.id"
          :id="'proj-row-' + project.id"
          class="d-flex align-items-center w-100 mass-notif-row mass-notif-row--user"
        >
          <b-form-checkbox
            :checked="isProjectSelected(project.id)"
            @change="(checked) => toggleProjectSelection(project, checked)"
            class="mass-notif-checkbox"
          >
            &nbsp;
          </b-form-checkbox>
          <span class="mass-notif-label">
            {{ project.name || `Project ${project.id}` }}
          </span>
        </div>

        <div
          v-if="!isLoading && projects.length === 0"
          class="text-center text-muted py-4"
        >
          No projects found.
        </div>
      </div>
    </b-overlay>

    <!-- Files are opt-in per project: nothing from a project's Files tab goes
         out unless it is chosen here (or later, from the share list). -->
    <div
      v-if="canChooseFiles && selectedProjectIds.length > 0"
      class="share-files-choice mt-3"
      data-test="share-files-choice"
    >
      <div class="share-files-choice__title">Project files to include</div>
      <div class="share-files-choice__hint">
        Nothing from a project's Files tab is shared unless you choose it.
      </div>
      <div
        v-for="project in selectedProjectsList"
        :key="'files-' + project.id"
        class="share-files-choice__row"
      >
        <span class="share-files-choice__name">{{
          project.name || `Project ${project.id}`
        }}</span>
        <span
          class="share-files-choice__count"
          :data-test="`files-count-${project.id}`"
          >{{ filesSummary(project.id) }}</span
        >
        <button
          type="button"
          class="share-files-choice__link"
          :disabled="submitting"
          :data-test="`choose-files-${project.id}`"
          @click="openFilesPicker(project)"
        >
          Choose files
        </button>
      </div>
    </div>

    <p
      v-if="selectedProjectIds.length === 0"
      class="mt-2 mb-0 text-muted small"
    >
      Select at least one project.
    </p>
    <p v-else-if="!isRecipientEmailValid" class="mt-2 mb-0 text-muted small">
      Enter a valid recipient email.
    </p>

    <b-pagination
      v-if="totalProjects > perPage"
      v-model="projectPage"
      :total-rows="totalProjects"
      :per-page="perPage"
      align="center"
      class="mt-3 mb-0"
      :disabled="isLoading || submitting"
    />

    <project-share-files-modal
      v-model="filesPickerOpen"
      :project-id="filesPickerProject && filesPickerProject.id"
      :project-name="(filesPickerProject && filesPickerProject.name) || ''"
      :selected-ids="filesPickerSelected"
      confirm-label="DONE"
      @confirm="onFilesChosen"
    />

    <template #modal-footer>
      <b-button
        variant="outline-secondary"
        :disabled="submitting"
        @click="closeModal"
      >
        Cancel
      </b-button>
      <b-button variant="primary" :disabled="!canSend" @click="onSend">
        Send
      </b-button>
    </template>
  </b-modal>
</template>

<script>
import _ from 'lodash'
import { mapGetters, mapState } from 'vuex'
import Project from '@/models/Project'
import ProjectShareFilesModal from '@/components/projects/ProjectShareFilesModal'

const EMAIL_SYNTAX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/

function normalizeUserType(payload) {
  if (!payload || typeof payload !== 'object') return null
  const user = payload.user || null
  const raw =
    payload.user_type ||
    (user && user.user_type) ||
    (user && user.role && typeof user.role === 'string' && user.role) ||
    null
  return raw != null && raw !== '' ? String(raw).toLowerCase() : null
}

function extractCompanyMeta(payload) {
  if (!payload || typeof payload !== 'object') {
    return { companyName: '', companyId: null }
  }
  const company =
    payload.company || (payload.user && payload.user.company) || null
  const companyName =
    (company && company.name) ||
    payload.company_name ||
    (payload.user && payload.user.company_name) ||
    ''
  let companyId = null
  if (company && company.id != null) {
    companyId = company.id
  } else if (payload.company_id != null) {
    companyId = payload.company_id
  } else if (payload.user && payload.user.company_id != null) {
    companyId = payload.user.company_id
  }
  return {
    companyName: typeof companyName === 'string' ? companyName : '',
    companyId,
  }
}

const RECIPIENT_STATUS = {
  IDLE: 'idle',
  TRADIE: 'tradie',
  OWN_COMPANY: 'own_company',
  BUSINESS_ADMIN: 'business_admin',
  UNKNOWN_EXISTING: 'unknown_existing',
  NEW: 'new',
  ERROR: 'error',
}

function getProjectTradieCount(project) {
  if (!project) return null
  const raw =
    project.tradies_count != null ? project.tradies_count : project.tradie_count
  if (raw === null || raw === undefined || raw === '') return null
  const n = Number(raw)
  return Number.isNaN(n) ? null : n
}

function projectHasNoTradies(project) {
  const count = getProjectTradieCount(project)
  return count === 0
}

function formatProjectNameList(names) {
  if (!names.length) return ''
  if (names.length === 1) return `"${names[0]}"`
  if (names.length === 2) return `"${names[0]}" and "${names[1]}"`
  const last = names[names.length - 1]
  const rest = names.slice(0, -1).map((n) => `"${n}"`)
  return `${rest.join(', ')}, and "${last}"`
}

export default {
  name: 'ShareProjectModal',

  components: { ProjectShareFilesModal },

  props: {
    showModal: {
      type: Boolean,
      default: false,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    shareAlertMessage: {
      type: String,
      default: '',
    },
    shareAlertVariant: {
      type: String,
      default: 'danger',
    },
  },

  data() {
    return {
      projectPage: 1,
      perPage: 10,
      projectSearch: '',
      isLoading: false,
      sharedToEmail: '',
      // id -> { id, name, tradies_count, tradie_count } so selections survive paging/searching
      selectedProjectsMap: {},
      recipientStatus: RECIPIENT_STATUS.IDLE,
      recipientCompanyName: '',
      isCheckingRecipientEmail: false,
      ownCompanyEmails: [],
      projects: [],
      totalProjects: 0,
      // projectId -> ids of the project files to include. Empty = none.
      fileSelections: {},
      filesPickerOpen: false,
      filesPickerProject: null,
    }
  },

  computed: {
    ...mapState('auth', {
      authUser: (state) => state.user,
    }),

    ...mapGetters('auth', ['hasPermission']),

    selectedProjectIds() {
      return Object.values(this.selectedProjectsMap).map((p) => p.id)
    },

    selectedProjectsList() {
      return Object.values(this.selectedProjectsMap)
    },

    // Choosing files needs permission to see them; without it nothing is
    // offered and nothing is shared.
    canChooseFiles() {
      return this.hasPermission('projects.files.view')
    },

    filesPickerSelected() {
      const project = this.filesPickerProject
      return (project && this.fileSelections[String(project.id)]) || []
    },

    ownCompanyEmailSet() {
      const set = new Set(
        (this.ownCompanyEmails || [])
          .filter(Boolean)
          .map((e) => e.toLowerCase()),
      )
      const myEmail = this.authUser && this.authUser.email
      if (myEmail) set.add(myEmail.toLowerCase())
      return set
    },

    show: {
      get() {
        return this.showModal
      },
      set(val) {
        if (!val && !this.submitting) {
          this.$emit('clearShareAlert')
          this.$emit('onCloseShareProjectModal')
        }
      },
    },

    allSelectedOnPage() {
      return (
        this.projects.length > 0 &&
        this.projects.every((p) => this.isProjectSelected(p.id))
      )
    },

    someSelectedOnPage() {
      return this.projects.some((p) => this.isProjectSelected(p.id))
    },

    isRecipientEmailValid() {
      return EMAIL_SYNTAX.test(this.sharedToEmail || '')
    },

    isRecipientOwnCompany() {
      return this.recipientStatus === RECIPIENT_STATUS.OWN_COMPANY
    },

    emailFieldState() {
      if (!this.sharedToEmail) return null
      if (!this.isRecipientEmailValid) return false
      if (this.isRecipientOwnCompany) {
        return false
      }
      return null
    },

    emailInvalidFeedback() {
      if (!this.sharedToEmail) return ''
      if (!this.isRecipientEmailValid) return 'Enter a valid email address.'
      if (this.isRecipientOwnCompany) {
        return "You can't share a project with your own company. Enter an email from another company."
      }
      return ''
    },

    recipientHint() {
      switch (this.recipientStatus) {
        case RECIPIENT_STATUS.BUSINESS_ADMIN:
          return this.recipientCompanyName
            ? `This project will be shared with company "${this.recipientCompanyName}".`
            : 'This email belongs to an existing business admin. The project will be shared with their company.'
        case RECIPIENT_STATUS.NEW:
          return 'No registered account was found. An invitation email will be sent with instructions on how to create an account and access these shared projects.'
        case RECIPIENT_STATUS.UNKNOWN_EXISTING:
          return 'This email is already registered. Send to continue - you will see an error if sharing is not allowed.'
        case RECIPIENT_STATUS.TRADIE:
          return 'This email is listed as a tradie account. You can still send; whether sharing is allowed is decided when you send.'
        case RECIPIENT_STATUS.ERROR:
        case RECIPIENT_STATUS.IDLE:
        default:
          return ''
      }
    },

    recipientHintClass() {
      switch (this.recipientStatus) {
        case RECIPIENT_STATUS.BUSINESS_ADMIN:
          return 'text-success'
        case RECIPIENT_STATUS.NEW:
          return 'text-primary'
        case RECIPIENT_STATUS.UNKNOWN_EXISTING:
          return 'text-muted'
        case RECIPIENT_STATUS.TRADIE:
          return 'text-warning'
        default:
          return 'text-muted'
      }
    },

    canSend() {
      return (
        this.isRecipientEmailValid &&
        !this.isRecipientOwnCompany &&
        !this.isCheckingRecipientEmail &&
        this.selectedProjectIds.length > 0 &&
        !this.isLoading &&
        !this.submitting
      )
    },

    selectedProjectsWithNoTradies() {
      return Object.values(this.selectedProjectsMap)
        .filter((p) => projectHasNoTradies(p))
        .map((p) => ({
          id: p.id,
          name: p.name || `Project ${p.id}`,
        }))
    },

    noTradiesInProjects() {
      return this.selectedProjectsWithNoTradies.length > 0
    },

    noTradiesInProjectsMessage() {
      const names = this.selectedProjectsWithNoTradies.map((p) => p.name)
      if (!names.length) return ''

      const list = formatProjectNameList(names)
      if (names.length === 1) {
        return `The selected project ${list} doesn't have any tradies. You can still share it, but the recipient won't see tradie information for that project.`
      }
      return `The selected projects ${list} don't have any tradies. You can still share them, but the recipient won't see tradie information for those projects.`
    },
  },

  watch: {
    sharedToEmail: _.debounce(function (newVal) {
      this.runRecipientEmailCheck(newVal)
    }, 500),

    projectSearch() {
      if (!this.show) return
      this.projectPage = 1
      this.fetchProjects()
    },

    projectPage(newVal, oldVal) {
      if (!this.show) return
      if (newVal === oldVal) return
      this.fetchProjects()
    },
  },

  methods: {
    async runRecipientEmailCheck(raw) {
      const email = (raw || '').trim()
      this.recipientCompanyName = ''
      this.recipientStatus = RECIPIENT_STATUS.IDLE

      if (!email || !EMAIL_SYNTAX.test(email)) {
        this.isCheckingRecipientEmail = false
        return
      }

      if (this.ownCompanyEmailSet.has(email.toLowerCase())) {
        this.recipientStatus = RECIPIENT_STATUS.OWN_COMPANY
        this.isCheckingRecipientEmail = false
        return
      }

      this._recipientEmailCheckGen = (this._recipientEmailCheckGen || 0) + 1
      const gen = this._recipientEmailCheckGen

      this.isCheckingRecipientEmail = true
      try {
        const { data } = await this.$api.post('auth/check-email', { email })
        if (gen !== this._recipientEmailCheckGen) return

        const payload = (data && data.data) || data || {}
        const userType = normalizeUserType(payload)
        const { companyName, companyId } = extractCompanyMeta(payload)

        const hasCompanyContext =
          Boolean(companyName) ||
          (companyId !== null && companyId !== undefined && companyId !== '')

        if (userType === 'tradie') {
          this.recipientStatus = RECIPIENT_STATUS.TRADIE
        } else if (userType === 'business_admin' || hasCompanyContext) {
          this.recipientCompanyName = companyName
          this.recipientStatus = RECIPIENT_STATUS.BUSINESS_ADMIN
        } else {
          this.recipientCompanyName = companyName
          this.recipientStatus = RECIPIENT_STATUS.UNKNOWN_EXISTING
        }
      } catch (err) {
        if (gen !== this._recipientEmailCheckGen) return
        const data = err && err.response && err.response.data
        const errorCode = data && data.error_code
        const emailIsNew =
          errorCode === 'EMAIL_NOT_FOUND' || errorCode === 'USERNAME_NOT_FOUND'
        if (emailIsNew) {
          this.recipientStatus = RECIPIENT_STATUS.NEW
        } else {
          this.recipientStatus = RECIPIENT_STATUS.ERROR
        }
      } finally {
        if (gen === this._recipientEmailCheckGen) {
          this.isCheckingRecipientEmail = false
        }
      }
    },

    async fetchProjects() {
      this._projectsFetchGen = (this._projectsFetchGen || 0) + 1
      const gen = this._projectsFetchGen
      this.isLoading = true
      try {
        const query = Project.page(this.projectPage).limit(this.perPage)
        if (this.projectSearch) {
          query.where('search', this.projectSearch)
        }
        const res = await query.get()
        if (gen !== this._projectsFetchGen) return

        const list = Array.isArray(res && res.data) ? res.data : []
        this.projects = list.filter((p) => !p.is_archived)

        const meta = (res && res.meta) || {}
        if (meta.total != null) {
          this.totalProjects = Number(meta.total) || 0
        } else if (meta.pagination && meta.pagination.total != null) {
          this.totalProjects = Number(meta.pagination.total) || 0
        } else {
          this.totalProjects = this.projects.length
        }
      } catch (err) {
        if (gen !== this._projectsFetchGen) return
        console.log('failed to fetch projects', err)
        this.projects = []
        this.totalProjects = 0
      } finally {
        if (gen === this._projectsFetchGen) {
          this.isLoading = false
        }
      }
    },

    onShown() {
      this.fileSelections = {}
      this.filesPickerOpen = false
      this.filesPickerProject = null
      this.selectedProjectsMap = {}
      this.projectSearch = ''
      this.projectPage = 1
      this.sharedToEmail = ''
      this.recipientStatus = RECIPIENT_STATUS.IDLE
      this.recipientCompanyName = ''
      this.isCheckingRecipientEmail = false
      this.fetchProjects()
      this.fetchOwnCompanyEmails()
    },

    async fetchOwnCompanyEmails() {
      try {
        const { data } = await this.$api.get('company/business-admins')
        const raw = (data && data.data) || data || []
        this.ownCompanyEmails = raw
          .map(
            (item) =>
              (item && item.email) || (item && item.user && item.user.email),
          )
          .filter(Boolean)
      } catch (err) {
        this.ownCompanyEmails = []
      }
    },

    isProjectSelected(id) {
      return Object.prototype.hasOwnProperty.call(
        this.selectedProjectsMap,
        String(id),
      )
    },

    toCompactProject(project) {
      return {
        id: project.id,
        name: project.name,
        tradies_count: project.tradies_count,
        tradie_count: project.tradie_count,
      }
    },

    toggleProjectSelection(project, checked) {
      const key = String(project.id)
      if (checked) {
        this.$set(this.selectedProjectsMap, key, this.toCompactProject(project))
      } else {
        this.$delete(this.selectedProjectsMap, key)
        this.$delete(this.fileSelections, key)
      }
    },

    filesSummary(projectId) {
      const n = (this.fileSelections[String(projectId)] || []).length
      if (n === 0) return 'No files'
      return n === 1 ? '1 file' : `${n} files`
    },

    openFilesPicker(project) {
      this.filesPickerProject = project
      this.filesPickerOpen = true
    },

    onFilesChosen(ids) {
      const project = this.filesPickerProject
      if (project) this.$set(this.fileSelections, String(project.id), ids)
      this.filesPickerOpen = false
    },

    toggleSelectAll(checked) {
      if (checked) {
        this.projects.forEach((p) => {
          this.$set(
            this.selectedProjectsMap,
            String(p.id),
            this.toCompactProject(p),
          )
        })
      } else {
        this.projects.forEach((p) => {
          this.$delete(this.selectedProjectsMap, String(p.id))
          this.$delete(this.fileSelections, String(p.id))
        })
      }
    },

    closeModal() {
      if (this.submitting) return
      this.$emit('clearShareAlert')
      this.$emit('onCloseShareProjectModal')
    },

    onDismissShareAlert() {
      this.$emit('clearShareAlert')
    },

    onSend() {
      if (!this.canSend) return
      const selected = Object.values(this.selectedProjectsMap)
      const ids = selected.map((p) => p.id)
      this.$emit('onShareProjects', {
        email: this.sharedToEmail.trim(),
        project_ids: ids,
        // projectId -> ids of the project files to include (none by default).
        project_files: ids.reduce((acc, id) => {
          const chosen = this.fileSelections[String(id)] || []
          if (chosen.length) acc[id] = chosen
          return acc
        }, {}),
        project_selection: selected.map((p) => ({
          id: p.id,
          name: p.name || `Project ${p.id}`,
        })),
        recipient_status: this.recipientStatus,
        recipient_company_name: this.recipientCompanyName || null,
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.mass-notif-row {
  height: 2.5rem;
  min-height: 2.5rem;
  padding: 0;

  &--user {
    background-color: #ffffff;
    border-top: 1px solid #ffffff;
  }
}

.mass-notif-checkbox {
  margin: 10px;
  width: 28px;
  min-width: 28px;
  margin-bottom: 15px;
}

.mass-notif-label {
  flex: 1;
  padding-left: 16px;
  text-align: left;
}

.mass-notif-select-all {
  white-space: nowrap;
}

.mass-notif-divider {
  margin: 4px 0 8px;
  border-top: 1px solid #dee2e6;
}

.share-project-modal-alert {
  white-space: pre-line;
}

.share-files-choice {
  padding: 12px 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #f9fafb;
  text-align: left;
}

.share-files-choice__title {
  font-weight: 700;
}

.share-files-choice__hint {
  margin-bottom: 8px;
  color: #6b7280;
  font-size: 0.875rem;
}

.share-files-choice__row {
  display: flex;
  align-items: center;
  padding: 6px 0;
}

.share-files-choice__name {
  flex: 1 1 auto;
  min-width: 0;
  word-break: break-word;
}

.share-files-choice__count {
  margin: 0 16px;
  color: #6b7280;
  white-space: nowrap;
}

// A text link, not a .btn (those have a fixed 56px height here).
.share-files-choice__link {
  padding: 0;
  border: 0;
  background: transparent;
  color: #006cff;
  font: inherit;
  white-space: nowrap;
  cursor: pointer;

  &:hover,
  &:focus {
    text-decoration: underline;
    outline: none;
  }

  &:disabled {
    color: #6b7280;
    cursor: default;
    text-decoration: none;
  }
}
</style>
